import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronUp, ChevronDown, Trash2, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";
import {
  createProductionStatus, updateProductionStatus, deleteProductionStatus, reorderProductionStatuses,
} from "@/lib/api";

const STATUS_PALETTE = ["#0E7490", "#0891B2", "#7C3AED", "#D97706", "#059669", "#0F766E", "#DC2626", "#DB2777", "#2563EB", "#65A30D", "#475569", "#EA580C"];

function ColorPicker({ value, onPick, testid }) {
  const { t } = useT();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 w-7 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: value }}
          aria-label={t("prod.pickColor")}
          data-testid={testid}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-6 gap-1.5">
          {STATUS_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onPick(c)}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-border"
              style={{ backgroundColor: c }}
              data-testid={`${testid}-swatch-${c}`}
            >
              {value === c && <Check className="h-3.5 w-3.5 text-white" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function StatusManagerDialog({ open, statuses, onClose, onChanged }) {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState(STATUS_PALETTE[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => { setItems(statuses.map((s) => ({ ...s }))); })();
  }, [open, statuses]);

  const saveLabel = async (s) => {
    const original = statuses.find((x) => x.id === s.id);
    if (!s.label.trim() || s.label === original?.label) return;
    try {
      await updateProductionStatus(s.id, { label: s.label.trim() });
      toast.success(t("prod.statusRenamed"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.renameFailed"));
      onChanged();
    }
  };
  const setColor = async (s, color) => {
    setItems((arr) => arr.map((x) => (x.id === s.id ? { ...x, color } : x)));
    try {
      await updateProductionStatus(s.id, { color });
      onChanged();
    } catch {
      toast.error(t("prod.colorFailed"));
    }
  };
  const toggleTerminal = async (s, v) => {
    setItems((arr) => arr.map((x) => (x.id === s.id ? { ...x, is_terminal: v } : x)));
    try {
      await updateProductionStatus(s.id, { is_terminal: v });
      onChanged();
    } catch {
      toast.error(t("prod.updateFailed"));
    }
  };
  const toggleMaturation = async (s, v) => {
    setItems((arr) => arr.map((x) => (x.id === s.id ? { ...x, is_maturation: v } : x)));
    try {
      await updateProductionStatus(s.id, { is_maturation: v });
      onChanged();
    } catch {
      toast.error(t("prod.updateFailed"));
    }
  };
  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try {
      await reorderProductionStatuses(arr.map((x) => x.id));
      onChanged();
    } catch {
      toast.error(t("prod.reorderFailed"));
    }
  };
  const remove = async (s) => {
    try {
      await deleteProductionStatus(s.id);
      toast.success(t("prod.statusDeleted"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.deleteFailed"));
    }
  };
  const add = async () => {
    if (!newLabel.trim()) {
      toast.error(t("prod.enterStatusName"));
      return;
    }
    setBusy(true);
    try {
      await createProductionStatus({ label: newLabel.trim(), color: newColor });
      toast.success(t("prod.statusAdded"));
      setNewLabel("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.failedAddStatus"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto thin-scroll" data-testid="status-manager-dialog">
        <DialogHeader>
          <DialogTitle>{t("prod.manageStatuses")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {t("prod.statusManagerHint")}
        </p>
        <div className="space-y-2">
          {items.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-2 rounded-lg border border-border p-2" data-testid={`status-manager-row-${idx}`}>
              <div className="flex flex-col">
                <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`status-move-up-${idx}`}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} data-testid={`status-move-down-${idx}`}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <ColorPicker value={s.color} onPick={(c) => setColor(s, c)} testid={`status-color-${idx}`} />
              <Input
                value={s.label}
                onChange={(e) => setItems((arr) => arr.map((x) => (x.id === s.id ? { ...x, label: e.target.value } : x)))}
                onBlur={() => saveLabel(s)}
                className="h-8 flex-1"
                data-testid={`status-label-input-${idx}`}
              />
              <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                <Switch checked={!!s.is_terminal} onCheckedChange={(v) => toggleTerminal(s, v)} data-testid={`status-terminal-${idx}`} />
                {t("prod.final")}
              </label>
              <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                <Switch checked={!!s.is_maturation} onCheckedChange={(v) => toggleMaturation(s, v)} data-testid={`status-maturation-${idx}`} />
                {t("prod.maturationLabel")}
              </label>
              <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`status-delete-${idx}`}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t border-border pt-3">
          <ColorPicker value={newColor} onPick={setNewColor} testid="status-new-color" />
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs">{t("prod.addCustomStatus")}</Label>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder={t("prod.statusPlaceholder")} className="h-8" data-testid="status-new-label-input" />
          </div>
          <Button size="sm" onClick={add} disabled={busy} data-testid="status-add-button">
            <Plus className="mr-1.5 h-4 w-4" /> {t("common.add")}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="status-manager-done">{t("common.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
